/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class GlobalFiltersImpl
 * @hideconstructor
 * @classdesc
 */

define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../api/FiltersAPI',
	'../../../filters/VisFilterProxy',
	'../../../filters/Filters'
], (_, APIFactory, FiltersAPI, FilterProxy, FilterCollection) => {

	/**
	 * list of members publicly exposed as part of the filter entry
	 */
	const FILTERENTRY_PROPS = [
		'id',
		'sourceId',
		'dataType',
		'usage',
		'origin',
		'scope',
		'hierarchyUniqueNames',
		'tupleSet',
		'exclude'
	];

	class Filters {

		constructor(dashboard, sourceId, scope) {
			this.dashboard = dashboard;
			this.dataSources = dashboard.getFeature('DataSources');
			this.sourceId = sourceId;

			if(!this.sourceId) {
				const dataSourceList = this.dataSources.getDataSourceList();
				const firstSourceId = dataSourceList && dataSourceList.length && dataSourceList[0].getId();
				this.sourceId = firstSourceId ? firstSourceId : this.sourceId;
			}

			this.scope = scope;
			this.logger = dashboard.getService('.Logger');

			this.api = APIFactory.createAPI(this, [FiltersAPI]);
		}

		destroy() {
			this.dashboard = null;
		}

		getAPI() {
			return this.api;
		}

		/**
		 * @deprecated
		 */
		getFiltersDesc() {
			throw 'This function is not implemented for global filters';
		}

		_convertFilters(filters, selectors) {
			const proxy = new FilterProxy({
				selectors: selectors,
				input: this._getPageContextApi(),
				output: filters,
				logger: this.logger
			});
			proxy.convertFilters();
		}

		getFilterList() {
			const filters = new FilterCollection();
			// convert global filters
			this._convertFilters(filters, {
				origin: 'filter',
				scope: this.scope || 'global',
				sourceId: this.sourceId,
				_strictMatch: true //don't ignore sourceId
			});
			return filters.toJSON();
		}

		filterExists(itemContext) {
			const pageContext = this._getPageContextApi();
			const pageContextSelector = this._getPageContextSelector(itemContext);
			return !!pageContext.getPageContextItem(pageContextSelector);
		}

		addFilter(itemContext, filterContext = {}, transactionToken) {
			return this._updateFilter('update', itemContext, filterContext, transactionToken);
		}

		editFilter(itemContext, filterContext = {}, transactionToken) {
			return this._updateFilter('replace', itemContext, filterContext, transactionToken);
		}

		removeFilter(itemContext, transactionToken) {
			const pageContext = this._getPageContextApi();
			const pageContextSelector = this._getPageContextSelector(itemContext);
			const pageContextItem = pageContext.getPageContextItem(pageContextSelector);
			if (pageContextItem) {
				const deleteItem = this._getDeleteItemSpec(pageContextItem);
				const filterSpec = this._getFilterSpec('delete');
				const options = this._applyTransactionToken(transactionToken);
				return pageContext.updateFilterContext(deleteItem, filterSpec, options);
			} else {
				return false;
			}
		}

		/**
		 * Add or edit a filter
		 * @private
		 * @function _updateFilter
		 * @param {Object} column object describing a filter metadata column object
		 * @param {string} column.sourceId Datasource of metadata column
		 * @param {string} column.itemId metadata column identfier
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values = []] Array of values applied to the filter
		 * @param {boolean} [filterContext.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @param {TransactionToken} transactionToken transaction token
		 * @returns {Object} filter entry
		 */
		_updateFilter(command, itemContext, filterContext = {}, transactionToken = null) {
			const pageContext = this._getPageContextApi();
			const filterItem = this._getFilterItemSpec(itemContext, filterContext);
			const filterSpec = this._getFilterSpec(command, filterContext);
			const options = this._applyTransactionToken(transactionToken);
			// keep consistent as what we did with the pageContextApi where pageContext actions should not be silent
			options.silent = false;
			const filterEntry = pageContext.updateFilterContext(filterItem, filterSpec, options);
			return _.pick(filterEntry, FILTERENTRY_PROPS);
		}

		/**
		 * Apply the transaction token to the filter context as the legacy undoRedoTransactionId
		 * @private
		 * @param {Object} filterContext
		 * @param {TransactionToken} transactionToken
		 */
		_applyTransactionToken(transactionToken) {
			let options = {};
			if (transactionToken && transactionToken.transactionId) {
				options = {
					payloadData: {
						undoRedoTransactionId: transactionToken.transactionId,
						transactionToken: transactionToken
					}
				};
			}
			return options;
		}

		/**
		 * Get the Page Context API of the PageContextService
		 * @function _getPageContextApi
		 * @returns {Object} Page Context API
		 */
		_getPageContextApi() {
			return this.dashboard.getFeature('PageContextService').getPageContext();
		}

		/**
		 * Get a filter metadata column with the given source and column id
		 * @private
		 * @function _getFilterMetadataColumn
		 * @param {string} sourceId	metadata source identifier
		 * @param {string} columnId	metadata column identifier
		 * @returns {Promise<Object>} metadata column of the give source and column id
		 */
		_getFilterMetadataColumn(columnId) {
			const dataSource = this.dataSources.getDataSource(this.sourceId);
			return dataSource.getMetadataColumn(columnId);
		}

		/**
		 * Generate the selector spec for selecting a page context item
		 * The selector is used for calling <tt>PageContextApi.getPageContextItem</tt>
		 * @private
		 * @function _getPageContextSelector
		 * @param {Object} itemContext {@link itemContext} object describing a filter source context
		 * @returns {Object} page context selector object
		 */
		_getPageContextSelector(itemContext) {
			itemContext.sourceId = itemContext.sourceId || this.sourceId;
			itemContext.scope = itemContext.scope || this.scope || 'global';
			itemContext._strictMatch = true; //don't ignore sourceId
			return itemContext;
		}

		/**
		 * Generate a filter item spec for add / edit, based on the given metadata column
		 * @private
		 * @function _getFilterItemSpec
		 * @param {Object} {Object} itemContext {@link itemContext} object
		 * @param {Object} [filterContext] context which describes the details of the filter
		 * @param {boolean} [filterContext.openViewOnLoad = false] Flag indicating whether to open the filter dialog
		 * @returns {Object} filter item spec
		 */
		_getFilterItemSpec(itemContext, filterContext) {
			itemContext = this._getPageContextSelector(itemContext);
			if (!itemContext.itemId && _.isArray(itemContext.itemIds)) {
				// TODO PageContext requires itemIds, hierarchyUniqueNames, and hierarchies while they all represent the
				// same thing. Should be cleaned when re-factor that part of code.
				itemContext.hierarchyUniqueNames = itemContext.itemIds;
				itemContext.hierarchies = _.map(itemContext.itemIds, itemId => {
					return {
						hierarchyUniqueName: itemId
					};
				});
			} else {
				const column = this._getFilterMetadataColumn(itemContext.itemId);
				itemContext = _.extend(itemContext, {
					itemName: column.getLabel(),
					dataType: column.getDataType(),
					usage: column.getType(),
					isHierarchy: column.isHierarchy(),
					isNamedSet: column.isNamedSet(),
					openViewOnLoad: (filterContext && filterContext.openViewOnLoad) || false
				});
			}

			return itemContext;
		}

		/**
		 * Generate a filter item spec for delete, based on the given <tt>PageContextEntry</tt>
		 * @private
		 * @function _getDeleteItemSpec
		 * @param {Object} pageContextEntry PageContextEntry to delete
		 * @returns the delete filter item spec
		 */
		_getDeleteItemSpec(pageContextEntry) {
			return {
				item: pageContextEntry,
				scope: this.scope || 'global',
				origin: 'filter'
			};
		}

		/**
		 * Generate a filter spec with the given command
		 * @private
		 * @function _getFilterSpec
		 * @param {string} [cmd = 'update'] Allowed commands are 'update', 'replace' and 'delete'
		 * @param {Object} [filterContext] context which describes the details of the filter
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {boolean} [filterContext.exclude] Flag indicating if the filter should be inverted
		 * @param {Object} [filterContext.condition] Range condition of the filter
		 */
		_getFilterSpec(cmd, filterContext) {
			let spec = {
				command: cmd || 'update',
				exclude: !!(filterContext && filterContext.exclude) //TODO check if setting exclude at this level in the
				//spec works for range conditions.
			};

			if(filterContext && filterContext.operator){
				return this._getFilterSpecByOperator(spec, filterContext);
			}

			if (filterContext && filterContext.values) {
				spec = _.extend(spec, {
					values: filterContext.values
				});
			} else if (filterContext && filterContext.condition) {
				spec = _.extend(spec, {
					condition: filterContext.condition
				});
			}
			return spec;
		}

		_getFilterSpecByOperator(spec, filterContext) {
			if(filterContext.operator === 'in' || filterContext.operator === 'notin'){
				spec = _.extend(spec, {
					values: filterContext.values
				});
				if (filterContext.operator === 'notin'){
					spec.exclude = true;
				}
			} else if(filterContext.operator === 'between'|| filterContext.operator === 'notbetween'){
				const condition = {
					from: filterContext.values[0].u,
					to: filterContext.values[1].u
				};
				spec = _.extend(spec, {
					condition: condition
				});
				if (filterContext.operator === 'notbetween'){
					condition.invert = true;
				}
			}else if(filterContext.operator === 'gt'){
				const condition = {
					from: filterContext.values[0].u,
					to:''
				};
				spec = _.extend(spec, {
					condition: condition
				});
			}else if(filterContext.operator === 'lt'){
				const condition = {
					from: '',
					to: filterContext.values[0].u
				};
				spec = _.extend(spec, {
					condition: condition
				});
			} //TODO Add support for 'gte' and  lte' operators? What conditions generate these operators in the product?
			return spec;
		}
	}
	return Filters;
});
